import moment from 'moment';

export * from './probe';

export { awl_transition_exec } from './awl_transition_exec';

/**
 * @deprecated The method should not be used
 */
export function fastCopy(obj: any): any {
    return JSON.parse(JSON.stringify(obj));
}

export function typedFastCopy<T>(obj: T): T {
    try {
        return JSON.parse(JSON.stringify(obj));
    } catch (error) {
        /**
         * if json parsing throws error, use es6 syntax to copy object
         * Converting circular structure to JSON --> starting at object with
         * constructor 'Object' | property 'parent' -> object with constructor 'Object' | property 'subTabs'
         * -> object with constructor 'Array' ---
         * index 0 closes the circle
         *
         */
        return { ...obj };
    }
}

export function removeFromArray(list: Array<any>, item: any): number {
    const i = list.indexOf(item);

    if (~i) {
        list.splice(i, 1);
    }

    return i;
}

export function addToArray(list: Array<any>, item: any, index: number) {
    const i = list.indexOf(item);

    if (!~i) {
        list.splice(index, 0, item);
    }
}

export function arrayUnique<T = any>(arr: Array<T>, searchIndex: (item: T, items: T[]) => number = null): T[] {
    if (!searchIndex) {
        searchIndex = (item, items) => items.indexOf(item);
    }

    return arr.filter(function (item, index) {
        return searchIndex(item, arr) >= index;
    });
}

export function arrayShuffle(array: Array<any>) {
    let currentIndex = array.length,
        temporaryValue,
        randomIndex;
    while (0 !== currentIndex) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex -= 1;
        temporaryValue = array[currentIndex];
        array[currentIndex] = array[randomIndex];
        array[randomIndex] = temporaryValue;
    }
    return array;
}

type collectionMatcher = (item: any) => boolean;

export function collectionMoveItemsUp(items: any[], fMatcher: collectionMatcher): void {
    collectionMoveItemsUpCallback(items, fMatcher, swapItemsInArray);
}

export function collectionMoveItemsDown(items: any[], fMatcher: collectionMatcher): void {
    collectionMoveItemsDownCallback(items, fMatcher, swapItemsInArray);
}

export function swapItemsInArray(arr: any[], indexA: number, indexB: number): void {
    arr[indexA] = arr.splice(indexB, 1, arr[indexA])[0];
}

export function collectionMoveItemsUpCallback(
    items: any[],
    fMatcher: collectionMatcher,
    fnSwap: (arr: any[], indexA: number, indexB: number) => void
): void {
    for (let index = 0; index < items.length; index++) {
        const item = items[index];

        if (fMatcher(item)) {
            if (index > 0 && !fMatcher(items[index - 1])) {
                fnSwap(items, index, index - 1);
            }
        }
    }
}

export function collectionMoveItemsDownCallback(
    items: any[],
    fMatcher: collectionMatcher,
    fnSwap: (arr: any[], indexA: number, indexB: number) => void
): void {
    for (let index = items.length - 1; index >= 0; index--) {
        const item = items[index];

        if (fMatcher(item)) {
            if (index < items.length - 1 && !fMatcher(items[index + 1])) {
                fnSwap(items, index, index + 1);
            }
        }
    }
}

export function padLeft(v: number, n: number, str: string = '0'): string {
    return (v < 0 ? '-' : '') + Array(n - String(Math.abs(v)).length + 1).join(str || '0') + Math.abs(v);
}

export function dateParse(v: moment.MomentInput, format: string, try_short_year: boolean = false): Date {
    let dt = null;

    format = format.replace(/dd/g, 'DD').replace(/yyyy/g, 'YYYY');

    let parsed = moment(v, format);

    if (parsed.isValid()) {
        dt = parsed.toDate();
    } else if (try_short_year) {
        const short_year_format = format.replace('YYYY', 'YY');

        parsed = moment(v, short_year_format);
        dt = parsed.toDate();
        dt.setFullYear(dt.getFullYear() - 100);
    }

    return dt!;
}

/**
 * Decimal adjustment of a number.
 * @url: https://developer.mozilla.org/en/docs/Web/JavaScript/Reference/Global_Objects/Math/round
 * @param {String}  type  The type of adjustment.
 * @param {Number}  value The number.
 * @param {Integer} exp   The exponent (the 10 logarithm of the adjustment base).
 * @returns {Number} The adjusted value.
 */
export function decimalAdjust(type: string, value: any, exp: number): number {
    const anyMath = Math as any;
    // If the exp is undefined or zero...
    if (typeof exp === 'undefined' || +exp === 0) {
        return anyMath[type](value);
    }
    // value as number
    value = +value;
    exp = +exp;
    // If the value is not a number or the exp is not an integer...
    if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
        return NaN;
    }
    // Shift
    // value as string[]
    value = value.toString().split('e');
    // value as string
    value = anyMath[type](+(value[0] + 'e' + (value[1] ? +value[1] - exp : -exp)));
    // Shift back
    // value as string[]
    value = value.toString().split('e');
    return +(value[0] + 'e' + (value[1] ? +value[1] + exp : exp));
}

/**
 * Formats the money value to be the correct decimal places
 * @param value that should get rounded
 * @returns rounded value
 */
export function moneyAdjust(value: number): number {
    return decimalAdjust('round', value, -2);
}

export function round10(value: number, exp = -1) {
    return decimalAdjust('round', value, exp);
}

export function upperCaseFirstLetter(str: string) {
    if (!str) return '';
    return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
}

export function lowerCaseFirstLetter(str: string) {
    if (!str) return '';

    return `${str.charAt(0).toLowerCase()}${str.slice(1)}`;
}

export function escapeHTML(string: string) {
    const entityMap = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&quot;',
        '/': '&#x2F;'
    };
    return string.replace(/[&<>"'\/]/g, function (s) {
        return entityMap[s];
    });
}

export function removeEmpty(obj: Record<string, any>) {
    const newObj = {};
    Object.keys(obj).forEach(key => {
        if (obj[key] === Object(obj[key])) {
            newObj[key] = removeEmpty(obj[key]);
        }
        if (obj[key] !== undefined && obj[key] !== null) {
            newObj[key] = obj[key];
        }
    });
    return newObj;
}
